import { useCallback, useEffect, useState } from 'react';

import sellarModule from '../../../api/modules/seller.module';
import useDebounce from '../../../hooks/useDebounce';
import { TransectionTableView } from './TransectionTable.view';

/**
 * Transaction history, the container.
 *
 * The request is unchanged: same `buildParams` stripping of empty values, same
 * `getSellerTransactionHistory` call, same param names (`page`, `limit`,
 * `search`, `status`, `start_date`, `end_date`), same 500ms debounce, same
 * response reading of `data.transactions` and `data.pagination`.
 *
 * The file name keeps its original spelling because other modules import this
 * path, and renaming it is a separate change from converting it.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PAID', label: 'Paid' },
];

export default function TransactionTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [sortBy] = useState('date');
  const [sortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const debouncedSearch = useDebounce(searchTerm, 500);

  // Helper: clean filters
  const buildParams = (params) =>
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== '' && value !== null && value !== undefined,
      ),
    );

  const fetchTransactions = useCallback(async () => {
    setError(null);
    try {
      const params = buildParams({
        page: currentPage,
        limit: 10,
        search: debouncedSearch,
        status: statusFilter,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });

      const { success, data, error } = await sellarModule.getSellerTransactionHistory(params);

      if (success) {
        setData(data?.transactions || []);
        setPagination(data?.pagination || { page: 1, limit: 10, total_pages: 0 });
      } else {
        console.error('Error fetching transactions:', error);
        setData([]);
        setPagination({ page: 1, limit: 10, total: 0 });
        setError(error || 'Could not load your transactions.');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setData([]);
      setError('Could not load your transactions.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, statusFilter, dateRange]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, sortBy, sortOrder]);

  return (
    <TransectionTableView
      rows={data}
      loading={loading}
      error={error}
      onRetry={fetchTransactions}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      status={statusFilter}
      onStatusChange={setStatusFilter}
      statusOptions={STATUS_OPTIONS}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      page={pagination.page}
      pageCount={pagination.total_pages || 0}
      pageSize={pagination.limit || 10}
      total={pagination.total ?? data.length}
      onPageChange={setCurrentPage}
    />
  );
}
